import { FileDocumentType } from '@nkr/enums/document-type';
import { IWatrRequest } from '@nkr/models/index';
import { DevMockReceiptInput, DevMockSlot } from './constants';

const REQUEST_FILE_TYPE_SSN = 8;

function hasSsnRecord(data: IWatrRequest): boolean {
    return (data.fileUploadRecords || []).some((r) => {
        const type = Number(r.fileType);
        return r.isActive !== false && !Number.isNaN(type) && type === REQUEST_FILE_TYPE_SSN;
    });
}

function money(n: number | undefined): string {
    if (n == null || !Number.isFinite(Number(n))) {
        return '';
    }
    return `$${Number(n).toFixed(2)}`;
}

function formatDisplayDate(raw: string | undefined): string {
    const s = String(raw || '').trim();
    if (!s) {
        return '';
    }
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
        return `${iso[2]}/${iso[3]}/${iso[1]}`;
    }
    return s;
}

export function buildWageMockSlots(
    data: IWatrRequest,
    opts?: { forceSsn?: boolean; forceTax?: boolean; forcePaystub?: boolean }
): DevMockSlot[] {
    const slots: DevMockSlot[] = [];
    const id = data.id;
    const name = (data.name || `${data.firstName || ''} ${data.lastName || ''}`).trim();

    if (opts?.forceSsn || hasSsnRecord(data)) {
        slots.push({
            key: `ssn-${id}`,
            docType: FileDocumentType.SsnVerification,
            lookupId: id,
            label: 'SS# Verification',
            kind: 'ssn',
            category: 'Wage',
            detail: name ? `Donor: ${name}` : 'Identity document for SSN verify',
            secondary: data.ssnDecrypted
                ? `Expected SSN ending …${String(data.ssnDecrypted).replace(/\D/g, '').slice(-4)}`
                : 'Used on Tax / Pay Stub verify'
        });
    }
    if (opts?.forceTax || data.taxDocumentFile) {
        const year = data.incomeIrsYear && data.incomeIrsYear > 1900 ? String(data.incomeIrsYear) : '';
        const gross = money(data.incomeIrsYearGross);
        slots.push({
            key: `tax-${id}`,
            docType: FileDocumentType.Tax,
            lookupId: id,
            label: 'Tax Document',
            kind: 'tax',
            category: 'Wage',
            detail: [year && `Tax year ${year}`, gross && `IRS gross ${gross}`].filter(Boolean).join(' · ')
                || 'W-2 / tax return for income verify',
            secondary: name || undefined
        });
    }
    if (opts?.forcePaystub || data.payStubFile) {
        const start = formatDisplayDate(data.incomePayStubStartDate);
        const end = formatDisplayDate(data.incomePayStubEndDate);
        const gross = money(data.incomePayStubGross);
        slots.push({
            key: `paystub-${id}`,
            docType: FileDocumentType.PayStub,
            lookupId: id,
            label: 'Pay Stub',
            kind: 'paystub',
            category: 'Wage',
            detail: [start && end && `${start} – ${end}`, gross && `Stub gross ${gross}`].filter(Boolean).join(' · ')
                || 'Pay statement for stub verify',
            secondary: name || undefined
        });
    }
    return slots;
}

export function buildReceiptMockSlots(
    fileIds: number[],
    receipts?: DevMockReceiptInput[]
): DevMockSlot[] {
    if (receipts && receipts.length) {
        return receipts.filter(r => !!r.fileId).map((r, index) => {
            const typeName = r.expenseTypeName || 'Expense';
            const amt = money(r.amount);
            const bits = [
                typeName,
                amt,
                r.tripName,
                formatDisplayDate(r.expenseDate)
            ].filter(Boolean);
            return {
                key: `receipt-${r.fileId}`,
                docType: FileDocumentType.Receipts,
                lookupId: r.fileId,
                label: `${typeName} receipt`,
                kind: 'receipt' as const,
                category: 'Expense' as const,
                detail: bits.join(' · ') || `Receipt ${index + 1}`,
                secondary: r.description || r.receiptFileName || `File #${r.fileId}`
            };
        });
    }

    return (fileIds || []).filter(id => !!id).map((fileId, index) => ({
        key: `receipt-${fileId}`,
        docType: FileDocumentType.Receipts,
        lookupId: fileId,
        label: `Receipt ${index + 1}`,
        kind: 'receipt' as const,
        category: 'Expense' as const,
        detail: `Expense receipt file #${fileId}`,
        secondary: 'Travel / DER verify'
    }));
}

export function isWageSlotKnownMissing(data: IWatrRequest, slot: DevMockSlot): boolean {
    if (slot.kind === 'ssn') {
        return !hasSsnRecord(data);
    }
    if (slot.kind === 'tax') {
        return !data.taxDocumentFile;
    }
    if (slot.kind === 'paystub') {
        return !data.payStubFile;
    }
    return false;
}

export async function probeMissingSlots(
    slots: DevMockSlot[],
    probe: (docType: number, lookupId: number) => Promise<boolean>,
    onProgress?: (done: number, total: number, current?: DevMockSlot) => void,
    knownMissing?: (slot: DevMockSlot) => boolean
): Promise<DevMockSlot[]> {
    const missing: DevMockSlot[] = [];
    const total = slots.length;
    for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        if (onProgress) {
            onProgress(i, total, slot);
        }
        if (knownMissing && knownMissing(slot)) {
            missing.push(slot);
            if (onProgress) {
                onProgress(i + 1, total, slot);
            }
            continue;
        }
        let ok = false;
        try {
            ok = await probe(slot.docType, slot.lookupId);
        } catch {
            ok = false;
        }
        if (!ok) {
            missing.push(slot);
        }
        if (onProgress) {
            onProgress(i + 1, total, slot);
        }
    }
    return missing;
}

export function isDismissedForRequest(requestId: number): boolean {
    try {
        return window.sessionStorage.getItem(`nkr.watr.devMockDocs.dismiss.${requestId}`) === '1';
    } catch {
        return false;
    }
}

export function dismissForRequest(requestId: number): void {
    try {
        window.sessionStorage.setItem(`nkr.watr.devMockDocs.dismiss.${requestId}`, '1');
    } catch { }
}
